import { prepareROSPayload } from '@services/utils.service';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { v4 } from 'uuid';
import { TeamsDataContext } from '../../context/TeamsContext';
import {
  createRosTemplate,
  deleteQuestion,
  deleteRosTemplate,
  getLiveStreamById,
  getTemplateTitle,
  updateLiveStreamSettings,
  updateLiveStreams
} from '@services/youtube-platform.service';
import { useRouter } from 'next/router';
import { getAuth } from '@services/identity.service';
import type {
  Interaction,
  InteractionColors,
  InteractionOptions,
  InteractionSettings,
  InteractionType,
  PanelButton,
  Session,
  Template
} from '@components/Presentation/types';
import useSlideHistory from '../hooks/useSlideHistory';
import { panelBtnEnum, defaultInteractionConfig, interactionTypeMap } from '../constants';
import { pushDataLayerForEvent } from '@lib/gtag';
import { getDefaultTitle, notify, getDefaultOptions } from '../helpers';
import useGlobalSettings from '../hooks/useGlobalSettings';
import useFetchThemes from '@components/themes/hooks/useFetchThemes';
import { type Theme, type ThemeFontFamily } from '@components/themes/store/ThemeStore';
import useThemesAPI from '@components/themes/hooks/useThemesAPI';
import { cloneDeep } from 'lodash';
import useFetchUserDetails from '../hooks/useFetchUserDetails';
import useAutoSave from '../hooks/useAutoSave';
import { db } from '../../../lib/db';
import { usePowerPointSlides } from '../hooks/usePowerPointSlides';
import { isActiveElementInputLike } from '../helpers/domUtils';

type TSlideHandlers = {
  applyToSimilarSlides?: () => void;
  onTitleChange?: (value: string) => void;
  onOptionChange?: (options: InteractionOptions[]) => void;
  updateTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updateSetting: (
    key: keyof InteractionSettings,
    value: any
  ) => void;
  updateColor: (
    key: keyof InteractionColors,
    value: string | boolean
  ) => void;
  addOption: () => void;
  addBulkOptions: (count: number) => void;
  updateOptions: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
    choiceCircleOptions?: InteractionOptions[]
  ) => void;
  updateOptionsByName: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  deleteOption: (index: number) => void;
  resetDesign: () => void;
};

type View = 'List' | 'Grid';
type SaveState = 'saved' | 'saving' | 'unsaved';

export type RoSPageContextType = {
  selectedTheme?: Theme
  selectedThemeId: string | undefined;
  isLoadingTemplates: boolean;
  saveState: SaveState
  selectedPanelBtn: PanelButton | '';
  view: View;
  toggleView: (view?: View) => void;
  toggleSelectedPanelBtn: (buttonName: PanelButton | '') => () => void;
  slides: Interaction[];
  setSlides: React.Dispatch<React.SetStateAction<Interaction[]>>;
  selectedSlideIndex: number;
  showGenerateWithAIModal: boolean;
  setShowGenerateWithAIModal: (show: boolean) => void;
  updateSelectedSlideIndex: (index: number) => void;
  switchSlide: (slide: Interaction, index: number) => () => void;
  addNewSlide: (interactionType: InteractionType, slide?: Interaction) => void;
  deleteSlide: (index: number) => void;
  batchDeleteSlides: (indices: number[]) => void;
  duplicateSlide: () => void;
  changeInteraction: (value: string | number) => void;
  selectedSlide: Interaction & { handler: TSlideHandlers };
  mergeOrReplaceTemplate: (
    template: Template,
    actionType: 'merge' | 'replace'
  ) => void;
  rosTitle: string;
  layoutMode: 'square' | 'rectangle';
  updateLayoutMode: (mode: 'square' | 'rectangle') => void
  updateRoSTitle: (value: string) => void;
  selectPresentation: (template: Template) => Promise<any>;
  unLinkPresentation: () => Promise<any>;
  saveTemplate: (customPayload?: any, isAutoSave?: boolean) => Promise<any>
  duplicateTemplate: () => void;
  deleteTemplate: () => void;
  session: Session | null;
  templateId: string | null;
  handleDnD: (firstIndex: number, secondIndex: number) => void;
  updateTheme: (theme: Theme) => void;
  onCopyInteractionLink: (index: number) => void;
  onCopyInteraction: (index: number) => void;
  onPasteInteraction: (index: number) => void;
  disableAutoFocus: boolean;
  setDisableAutoFocus: (value: boolean) => void;
  selectedSlideIds: Record<string, string>;
  setSelectedSlideIds: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isAutoSaveEnabled: boolean;
  setIsAutoSaveEnabled: (enabled: boolean) => void;
  isOffline: boolean;
  retryCount: number;
  lastLocalSave: Date | null;
  deletePendingSlides: () => Promise<void>;
  handleMultiDnD: (sourceIndices: number[], targetIndex: number) => void;
  presentation: {
    isDirty: () => void;
    isClean: () => void;
    isSaving: () => void;
  };
  applySelectedTransitionToAllSlides: () => void;
  addSlides: (interactions: PowerpointSlideInfo[]) => Promise<void>;
};

type PowerpointSlideInfo = {
  description: string;
  interaction: {
    title: string;
    type: InteractionType;
    options?: Array<string>;
  };
  slide_number: number;
};

export const RoSPageContext = createContext<RoSPageContextType>({
  isLoadingTemplates: false,
  showGenerateWithAIModal: false,
  setShowGenerateWithAIModal: () => {},
  saveState: 'saved',
  templateId: null,
  selectedPanelBtn: '',
  toggleSelectedPanelBtn: () => () => {},
  view: 'List',
  toggleView: () => {},
  slides: [],
  setSlides: () => {},
  selectedSlideIndex: 0,
  updateSelectedSlideIndex: () => {},
  switchSlide: () => () => {},
  addNewSlide: () => {},
  deleteSlide: () => {},
  duplicateSlide: () => {},
  changeInteraction: () => {},
  // @ts-expect-error - default value
  selectedSlide: {},
  mergeOrReplaceTemplate: () => {},
  rosTitle: '',
  updateRoSTitle: () => {},
  selectPresentation: () => Promise.resolve(),
  unLinkPresentation: () => Promise.resolve(),
  saveTemplate: () => Promise.resolve(),
  duplicateTemplate: () => {},
  deleteTemplate: () => {},
  session: null,
  layoutMode: 'rectangle',
  updateLayoutMode: () => {},
  updateTheme: () => {},
  handleDnD: () => {},
  onCopyInteractionLink: () => {},
  onCopyInteraction: () => {},
  onPasteInteraction: () => {},
  selectedSlideIds: {},
  setSelectedSlideIds: () => {},
  undo: () => {},
  redo: () => {},
  canUndo: false,
  canRedo: false,
  isAutoSaveEnabled: true,
  setIsAutoSaveEnabled: () => {},
  isOffline: false,
  retryCount: 0,
  lastLocalSave: null,
  deletePendingSlides: async () => {},
  handleMultiDnD: () => {},
  presentation: {
    isDirty: () => {},
    isClean: () => {},
    isSaving: () => {}
  },
  applySelectedTransitionToAllSlides: () => {},
});

interface RoSContextProps {
  children: React.ReactNode;
  // toggleShowTemplate: (show: boolean) => void;
}

type ThemeConfig = {
  textFontFamily: {
    pollWithOptionsFontStyle?: string;
    pollFontStyle?: string;
    fantasticFansFontStyle?: string;
    textTrackFontStyle?: string;
    transientThoughtFontStyle?: string;
    wordCloudFontStyle?: string;
    multipleChoiceFontStyle?: string;
  };
  headingFontFamily: {
    pollWithOptionsHeadingFont?: string;
    pollHeadingFont?: string;
    fantasticFansHeadingFont?: string;
    textTrackHeadingFont?: string;
    transientThoughtHeadingFont?: string;
    wordCloudHeadingFont?: string;
    emojisEverywhereHeadingFont?: string;
    mapHeadingFont?: string;
    wheelHeadingFont?: string;
    talkingTilesHeadingFont?: string;
    newWheelHeadingFont?: string;
    linkLibraryHeadingFont?: string;
    quickQuestionsHeadingFont?: string;
    ratingPollsHeadingFont?: string;
    multipleChoiceHeadingFont?: string;
  };
  images: {
    pollWithOptionsImageSrc?: string;
    pollWithOptionsImageOpacity?: number;
    pollImageSrc?: string;
    pollImageOpacity?: number;
    ffImageSrc?: string;
    ffImageOpacity?: number;
    textTrackImageSrc?: string;
    textTrackImageOpacity?: number;
    ttImageSrc?: string;
    ttImageOpacity?: number;
    wcImageSrc?: string;
    wcImageOpacity?: number;
    emojisEverywhereImageSrc?: string;
    emojisEverywhereImageOpacity?: number;
    mapImageSrc?: string;
    mapImageOpacity?: number;
    choiceCircleImageSrc?: string;
    choiceCircleImageOpacity?: number;
    talkingTilesImageSrc?: string;
    talkingTilesImageOpacity?: number;
    winningWheelImageSrc?: string;
    winningWheelImageOpacity?: number;
    linkLibraryImageSrc?: string;
    linkLibraryImageOpacity?: number;
    quickQuestionImageSrc?: string;
    quickQuestionImageOpacity?: number;
    ratingPollsImageSrc?: string;
    ratingPollsImageOpacity?: number;
    multipleChoiceImageSrc?: string;
    multipleChoiceImageOpacity?: number;
  };
  default: {
    headingFontFamily?: string;
    imageSrc?: string;
    imageOpacity?: number;
  };
  settings?: {
    mapConfig?: any;
  };
  colors: Record<string, any>;
};

function RoSContext({ children }: RoSContextProps) {
  const [template, setTemplate] = useState<Template | null>(null);
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(undefined);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [rosTitle, setRosTitle] = useState<string>('Untitled Presentation');
  const [slides, setSlides] = useState<Interaction[]>([]);
  const [isEditable, setIsEditable] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const router = useRouter();
  const [templateId, setTemplateId] = useState<string | null>(
    (router.query?.templateId as string) || null
  );
  const { getPresentationInfo, presentationInfoError } = usePowerPointSlides();

  // Clear all presentations from IndexedDB when there's no templateId in the URL
  // Clear presentations when component unmounts or route changes
  useEffect(() => {
    const clearPresentations = async () => {
      try {
        await db.clearAllPresentations();
        // console.log('Successfully cleared all presentations from IndexedDB');
      } catch (error) {
        console.error('Failed to clear presentations from IndexedDB:', error);
      }
    };

    // Clear on route change
    router.events.on('routeChangeStart', clearPresentations);

    // Clear on component unmount
    return () => {
      clearPresentations();
      router.events.off('routeChangeStart', clearPresentations);
    };
  }, []);

  const deletedSlides = useRef<string[]>([]);
  const [view, setView] = useState<'List' | 'Grid'>('List');
  const [selectedSlideIndex, setSelectedSlideIndex] = useState<number>(-1);
  const [selectedPanelBtn, setSelectedPanelBtn] = useState<PanelButton | ''>(
    ''
  );
  const slideHistory = useSlideHistory();
  const { teamContext, user } = useContext(TeamsDataContext);
  const auth = getAuth();
  const { getGlobalSettings, getGlobalColors, selectedTheme } = useGlobalSettings(selectedThemeId || '');
  const { userThemes = [], defaultThemes = [], defaultThemeId, darkThemeId } = useFetchThemes();
  const allThemes = [...userThemes, ...defaultThemes];
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const [layoutMode, setLayoutMode] = useState<'square' | 'rectangle'>(user?.user_detail?.settings?.layoutMode || 'rectangle');

  const { data } = useFetchUserDetails();

  // Update layoutMode when user settings change, but only for new presentations (no templateId)
  useEffect(() => {
    if (!templateId && data?.entity?.settings?.layoutMode && data.entity.settings.layoutMode !== layoutMode) {
      setLayoutMode(data.entity.settings.layoutMode);
    }
  }, [data?.entity?.settings?.layoutMode, layoutMode, templateId]);

  type UndoRedoState = {
    slides: Interaction[];
    selectedIndex: number;
    themeId?: string;
    changeType: 'slide' | 'theme' | 'both';
    panelState: PanelButton | '';
  };

  const undoStack = useRef<UndoRedoState[]>([]);
  const redoStack = useRef<UndoRedoState[]>([]);
  const isUndoRedoInProgress = useRef<boolean>(false);
  const pushHistoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const undoRedoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const originalUndoStackCurrent = undoStack.current;

  // Wrap array methods to detect mutations
  const originalPush = originalUndoStackCurrent.push.bind(originalUndoStackCurrent);
  const originalPop = originalUndoStackCurrent.pop.bind(originalUndoStackCurrent);
  const originalSplice = originalUndoStackCurrent.splice.bind(originalUndoStackCurrent);

  originalUndoStackCurrent.push = function (...items: UndoRedoState[]) {
    return originalPush(...items);
  };

  originalUndoStackCurrent.pop = function () {
    return originalPop();
  };

  originalUndoStackCurrent.splice = function (start: number, deleteCount?: number, ...items: UndoRedoState[]) {
    return originalSplice(start, deleteCount || 0, ...items);
  };

  Object.defineProperty(undoStack, 'current', {
    get: () => originalUndoStackCurrent,
    set: (newValue) => {
      originalUndoStackCurrent.length = 0;
      if (newValue && newValue.length > 0) {
        originalUndoStackCurrent.push(...newValue);
      }
    }
  });

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const savePointIndex = useRef<number>(-1); // Track the save point in undo history
  const hasUndoClearedAfterSave = useRef<boolean>(false); // Track if we've cleared undo after last save

  const pushToHistory = async (changeType: 'slide' | 'theme' | 'both' = 'slide') => {
    // Don't push to history if an undo/redo operation is in progress
    if (isUndoRedoInProgress.current) return;

    // Add a small debounce to prevent multiple history entries for rapid changes
    if (pushHistoryTimeoutRef.current) {
      clearTimeout(pushHistoryTimeoutRef.current);
    }

    pushHistoryTimeoutRef.current = setTimeout(async () => {
      // Push current state to history
      await db.pushHistory('undo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType,
        panelState: selectedPanelBtn
      });

      setCanUndo(true);
      setCanRedo(false);
      pushHistoryTimeoutRef.current = null;
    }, 100); // Small delay to debounce rapid changes
  };

  const undo = async () => {
    // Prevent rapid consecutive undo operations
    if (isUndoRedoInProgress.current) return;

    // Cancel any pending history pushes
    if (pushHistoryTimeoutRef.current) {
      clearTimeout(pushHistoryTimeoutRef.current);
      pushHistoryTimeoutRef.current = null;
    }

    // Cancel any pending undo/redo operations
    if (undoRedoTimeoutRef.current) {
      clearTimeout(undoRedoTimeoutRef.current);
    }

    // Set flag to prevent auto-save during undo
    isUndoRedoInProgress.current = true;

    try {
      // Get the latest state from undo history
      const previousState = await db.getLatestHistory('undo');
      if (!previousState) {
        isUndoRedoInProgress.current = false;
        return;
      }

      // Remove the state from undo history
      await db.removeLatestHistory('undo');

      // Save current state to redo history
      await db.pushHistory('redo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType: previousState.changeType,
        panelState: selectedPanelBtn
      });

      // Apply the previous state based on change type
      if (previousState.changeType === 'theme') {
        // This was only a theme change - restore just the theme
        if (previousState.themeId !== undefined && previousState.themeId !== selectedThemeId) {
          const theme = [...userThemes, ...defaultThemes].find((t: Theme) => t.id === previousState.themeId);
          if (theme) {
            updateTheme(theme);
          }
        }
      } else if (previousState.changeType === 'slide') {
        // This was only a slide change - restore just the slides
        // Skip if this would restore a deleted slide
        if (previousState.slides.length <= slides.length) {
          setSlides(() => previousState.slides);
          setSelectedSlideIndex(previousState.selectedIndex);
        }
      } else {
        // This was a 'both' type change - restore everything
        // Skip if this would restore a deleted slide
        if (previousState.slides.length <= slides.length) {
          setSlides(() => previousState.slides);
          setSelectedSlideIndex(previousState.selectedIndex);
        }

        if (previousState.themeId !== undefined && previousState.themeId !== selectedThemeId) {
          const theme = [...userThemes, ...defaultThemes].find((t: Theme) => t.id === previousState.themeId);
          if (theme) {
            updateTheme(theme);
          }
        }
      }

      setSelectedSlideIds({});
      const hasMoreUndoStates = await db.getLatestHistory('undo') !== undefined;
      setCanUndo(hasMoreUndoStates);
      setCanRedo(true);

      // Restore the panel state
      if (previousState.panelState !== undefined) {
        setSelectedPanelBtn(previousState.panelState as PanelButton | '');
      }
    } catch (error) {
      console.error('Error during undo operation:', error);
    }

    // Use setTimeout to reset the flag after state updates are complete
    undoRedoTimeoutRef.current = setTimeout(() => {
      isUndoRedoInProgress.current = false;
      undoRedoTimeoutRef.current = null;
      // Mark as dirty after undo so auto-save can trigger
      presentation.isDirty();
    }, 300);
  };

  const redo = async () => {
    // Prevent rapid consecutive redo operations
    if (isUndoRedoInProgress.current) return;

    // Cancel any pending history pushes
    if (pushHistoryTimeoutRef.current) {
      clearTimeout(pushHistoryTimeoutRef.current);
      pushHistoryTimeoutRef.current = null;
    }

    // Cancel any pending undo/redo operations
    if (undoRedoTimeoutRef.current) {
      clearTimeout(undoRedoTimeoutRef.current);
    }

    // Set flag to prevent auto-save during redo
    isUndoRedoInProgress.current = true;

    try {
      // Get the latest state from redo history
      const nextState = await db.getLatestHistory('redo');
      if (!nextState) {
        isUndoRedoInProgress.current = false;
        return;
      }

      // Remove the state from redo history
      await db.removeLatestHistory('redo');

      // Save current state to undo history
      await db.pushHistory('undo', {
        slides: [...slides],
        selectedIndex: selectedSlideIndex,
        themeId: selectedThemeId,
        changeType: nextState.changeType,
        panelState: selectedPanelBtn
      });

      // Apply the next state based on change type
      if (nextState.changeType === 'theme') {
        // This was only a theme change - restore just the theme
        if (nextState.themeId !== undefined && nextState.themeId !== selectedThemeId) {
          const theme = [...userThemes, ...defaultThemes].find((t: Theme) => t.id === nextState.themeId);
          if (theme) {
            updateTheme(theme);
          }
        }
      } else if (nextState.changeType === 'slide') {
        // This was only a slide change - restore just the slides
        // Skip if this would restore a deleted slide
        if (nextState.slides.length <= slides.length) {
          setSlides(() => nextState.slides);
          setSelectedSlideIndex(nextState.selectedIndex);
        }
      } else {
        // This was a 'both' type change - restore everything
        // Skip if this would restore a deleted slide
        if (nextState.slides.length <= slides.length) {
          setSlides(() => nextState.slides);
          setSelectedSlideIndex(nextState.selectedIndex);
        }

        if (nextState.themeId !== undefined && nextState.themeId !== selectedThemeId) {
          const theme = [...userThemes, ...defaultThemes].find((t: Theme) => t.id === nextState.themeId);
          if (theme) {
            updateTheme(theme);
          }
        }
      }

      setSelectedSlideIds({});
      setCanUndo(true);
      const hasMoreRedoStates = await db.getLatestHistory('redo') !== undefined;
      setCanRedo(hasMoreRedoStates);

      // Restore the panel state
      if (nextState.panelState !== undefined) {
        setSelectedPanelBtn(nextState.panelState as PanelButton | '');
      }
    } catch (error) {
      console.error('Error during redo operation:', error);
    }

    // Use setTimeout to reset the flag after state updates are complete
    undoRedoTimeoutRef.current = setTimeout(() => {
      isUndoRedoInProgress.current = false;
      undoRedoTimeoutRef.current = null;
      // Mark as dirty after redo so auto-save can trigger
      presentation.isDirty();
    }, 300);
  };

  const fetchTemplate = async (id: string) => {
    setIsLoadingTemplates(true);
    const templateRes = await getTemplateTitle(id, auth, teamContext?.oid);
    if (!templateRes.status && templateRes.message === 'Presentation not found!') {
      notify('Template not found!', 'error');
      router.replace('/presentations');
      return;
    }
    if (templateRes.status) {
      setSelectedThemeId(templateRes.entity.selected_theme_id);
      setTemplate(templateRes.entity);
      setRosTitle(templateRes.entity.title);
      setIsEditable(templateRes.entity.editable);
      setLayoutMode(templateRes.entity.layout_mode || data?.entity?.settings?.layoutMode || 'rectangle');
      let newSlides = templateRes.entity.questions || [];

      // Load deleted slides from Dexie for this template
      const loadDeletedSlides = async () => {
        try {
          const backup = await db.getPresentation(id);
          if (backup?.deletedSlides) {
            deletedSlides.current = backup.deletedSlides;
          }
        } catch (e) {
          console.error('Failed to load deleted slides from Dexie:', e);
        }
      };
      await loadDeletedSlides();

      // Ensure all slides have transition_type set
      newSlides = newSlides.map((question: Interaction) => ({
        ...question,
        transition_type: question.transition_type || 'none' // Ensure transition_type is set
      }));

      if (router.query?.type === 'onboarding' && router.query?.templateId) {
        newSlides = newSlides?.map((question: Interaction) => {
          if (!['PDF Slide', 'connectSlide'].includes(question.type)) {
            question.setting = { ...(question.setting || {}), ...getGlobalSettings(question.type) };
            question.color = { ...(question.color || {}), ...getGlobalColors(question.type) };
          }
          if (['pollWithOptions', 'wheel',].includes(question.type) && question.options.length < 2) {
            if (question.options.length === 1) question.options.push({ id: v4(), body: 'Option 2' });
            else {
              question.options = getDefaultOptions(question.type);
            }
          }

          return ({
            ...question,
            id: undefined,
            transition_type: question.transition_type || 'none' // Ensure transition_type is set
          });
        });
      }
      setSlides(newSlides);
      if (newSlides.length > 0) {
        if (!['PDF Slide', 'connectSlide'].includes(newSlides[0].type)) {
          setSelectedPanelBtn('Content');
        } else setSelectedPanelBtn('');
        setSelectedSlideIndex(0);
      }
    }
    setIsLoadingTemplates(false);
    return templateRes;
  };
  const fetchSession = async () => {
    const fetchedSession = await getLiveStreamById(router.query.streamId, auth);
    if (!fetchedSession.status) return;
    setSession(fetchedSession.entity);
    if (fetchedSession.entity.template_id) {
      fetchTemplate(fetchedSession.entity.template_id);
    }
    setTemplateId(fetchedSession.entity.template_id);
    // if (!router.query.modal) {
    //   toggleShowTemplate(fetchedSession.entity.template_id === templateId);
    // }
  };

  const presentation = {
    isDirty: () => {
      setSaveState('unsaved');
    },
    isClean: () => {
      setSaveState('saved');
    },
    isSaving: () => {
      setSaveState('saving');
    }
  };

  useEffect(() => {
    if (router.query?.streamId && !templateId) { // if user routes from the session page
      fetchSession();
    }
    if (router.query?.templateId) { // if user edits a template
      fetchTemplate(router.query.templateId as string);
    }
    if (router.query?.type === 'onboarding' && !router.query?.templateId) { // setting map as default interaction for onboarding
      setSlides([
        {
          ...defaultInteractionConfig,
          color: {
            ...defaultInteractionConfig.color,
            ...getGlobalColors('map')
          },
          setting: {
            ...defaultInteractionConfig.setting,
            ...getGlobalSettings('map')
          },
          type: 'map'
        }
      ]);
      setSelectedSlideIndex(0);
      setSelectedPanelBtn('Content');
      // Set the default theme ID when creating a new presentation
      const defaultThemeToUse = defaultThemeId || darkThemeId;
      if (defaultThemeToUse) {
        setSelectedThemeId(defaultThemeToUse);
      }
      presentation.isDirty();
    }
    if (router.query?.type === 'onboarding' && router.query?.templateId) { // if user edits a template from onboarding
      setTemplateId(router.query?.templateId as string);
    }
    // Initialize new presentation without onboarding or newUser
    if (!router.query?.templateId && !router.query?.type && !router.query?.streamId && !router.query?.newUser && router.pathname === '/create-presentation') {
      // Set the default theme ID when creating a new presentation
      const defaultThemeToUse = defaultThemeId || darkThemeId;
      if (defaultThemeToUse) {
        setSelectedThemeId(defaultThemeToUse);
      }
      presentation.isDirty();
      // Immediately trigger a save to create the templateId
      saveTemplate();
    }
  }, [router.query, defaultThemeId, darkThemeId]);

  const reset = async () => {
    setRosTitle('Untitled Presentation');
    setSlides([]);
    if (templateId) {
      await db.clearHistory();
      // Clear deleted slides in Dexie
      try {
        const backup = await db.getPresentation(templateId);
        if (backup) {
          await db.savePresentation({
            ...backup,
            deletedSlides: []
          });
        }
      } catch (e) {
        console.error('Failed to clear deleted slides in Dexie:', e);
      }
    }
    setTemplateId(null);
    setIsEditable(true);
    setSelectedSlideIndex(-1);
    setSelectedPanelBtn('');
    slideHistory.flush();
    deletedSlides.current = [];
  };

  const updateRoSTitle = (value: string) => {
    setRosTitle(value);
    presentation.isDirty();
  };

  const toggleView = (view?: View) =>
    setView((prev) => view || (prev === 'List' ? 'Grid' : 'List'));

  const toggleSelectedPanelBtn = (buttonName: PanelButton | '') => () =>
    setSelectedPanelBtn(buttonName);

  const mergeOrReplaceTemplate = async (
    template: Template,
    actionType: 'merge' | 'replace'
  ) => {
    let title = rosTitle;
    if (rosTitle === 'Untitled Presentation') title = `${template.title}-copy`;
    const newSlides = template.questions!.map((question) => {
      if (!['PDF Slide', 'connectSlide'].includes(question.type)) {
        question.setting = { ...(question.setting || {}), ...getGlobalSettings(question.type) };
        question.color = { ...(question.color || {}), ...getGlobalColors(question.type) };
      }
      if (['pollWithOptions', 'wheel',].includes(question.type) && question.options.length < 2) {
        if (question.options.length === 1) question.options.push({ id: v4(), body: 'Option 2' });
        else {
          question.options = getDefaultOptions(question.type);
        }
      }

      return ({
        ...question,
        id: undefined,
        transition_type: question.transition_type || 'none' // Ensure transition_type is set
      });
    });
    if (actionType === 'merge') {
      const payload = prepareROSPayload(
        {
          id: templateId,
          title: title || 'Untitled presentation',
          createdBy: template?.created_by || auth.id || null,
          isEditable,
        },
        [...slides, ...newSlides],
        teamContext?.oid,
        [],
        !isEditable
      );
      const res = await saveTemplate(payload);
      if (!res.status) return;
      setSelectedSlideIndex(slides.length);
      setSelectedPanelBtn(
        ['PDF Slide', 'connectSlide'].includes(res.entity.questions[slides.length]?.type) ? '' : 'Content'
      );
    }
    if (actionType === 'replace') {
      slideHistory.flush();
      if (isEditable) {
        // careful with this, it will delete all the slides from sample template
        slides.forEach((slide) => {
          if (slide.id) deletedSlides.current.push(slide.id);
        });
      }
      const payload = prepareROSPayload(
        {
          id: templateId,
          title: title || 'Untitled presentation',
          createdBy: template?.created_by || auth.id || null,
          isEditable
        },
        [...newSlides],
        teamContext?.oid,
        [],
        !isEditable
      );
      const res = await saveTemplate(payload);
      if (!res.status) return;
      setSelectedSlideIndex(0);
      setSelectedPanelBtn(
        ['PDF Slide', 'connectSlide'].includes(res.entity.questions[0]?.type) ? '' : 'Content'
      );
    }
  };

  const selectPresentation = async (template: Template) => {
    if (!session) return;
    // Set theme ID before making any API calls
    const themeIdToUse = template.selected_theme_id || selectedThemeId;
    setSelectedThemeId(themeIdToUse);

    const response = await updateLiveStreams(session.id, {
      templateId: template.id,
      selected_theme_id: themeIdToUse // Add theme ID to update call
    });

    if (response.status) {
      setTemplateId(template.id);
      setRosTitle(template.title);

      // Load deleted slides from Dexie for this template
      const loadDeletedSlides = async () => {
        try {
          const backup = await db.getPresentation(template.id);
          if (backup?.deletedSlides) {
            deletedSlides.current = backup.deletedSlides;
          }
        } catch (e) {
          console.error('Failed to load deleted slides from Dexie:', e);
        }
      };
      await loadDeletedSlides();

      let filteredQuestions = template.questions || [];

      // Get current backup to check for deleted slides
      const backup = await db.getPresentation(template.id);
      if (backup?.deletedSlides) {
        try {
          deletedSlides.current = backup.deletedSlides;
          // Filter out deleted slides and ensure transition_type is set
          filteredQuestions = filteredQuestions.filter((slide: Interaction) => {
            return !slide.id || !deletedSlides.current.includes(slide.id);
          }).map((slide: Interaction) => ({
            ...slide,
            transition_type: slide.transition_type || 'none' // Ensure transition_type is set
          }));
        } catch (e) {
          console.error('Failed to parse deleted slides:', e);
        }
      } else {
        // Ensure transition_type is set even when there are no deleted slides
        filteredQuestions = filteredQuestions.map((slide: Interaction) => ({
          ...slide,
          transition_type: slide.transition_type || 'none' // Ensure transition_type is set
        }));
      }

      setSlides(filteredQuestions);
      setIsEditable(template.editable);
      setSelectedSlideIndex(0);
      if (filteredQuestions.length > 0 && !['PDF Slide', 'connectSlide'].includes(filteredQuestions[0]?.type)) {
        setSelectedPanelBtn('Content');
      } else {
        setSelectedPanelBtn('');
      }

      // Apply theme if it exists
      if (themeIdToUse) {
        const themeToApply = [...userThemes, ...defaultThemes].find(theme => theme.id === themeIdToUse);
        if (themeToApply) {
          updateTheme(themeToApply);
        }
      }

      pushDataLayerForEvent('update ros template', { templateId: template.id });
      notify('Presentation selected.');
    } else {
      notify('Something went wrong. Please try again.', 'error');
    }
    return response;
  };

  const unLinkPresentation = async () => {
    if (!session) return;
    const res = await updateLiveStreams(session.id, { templateId: null });
    if (res.status) {
      // Clear backup before unlinking
      if (templateId) {
        try {
          await db.deletePresentation(templateId);
          console.log('Successfully cleared backup for template:', templateId);
        } catch (error) {
          console.error('Failed to clear backup:', error);
        }
      }
      reset();
      fetchSession();
      notify('Template unlinked.');
      // toggleShowTemplate(true);
    } else {
      notify('Something went wrong. Please try again.', 'error');
    }
    return res;
  };

  const deletePendingSlides = async () => {
    if (deletedSlides.current.length > 0) {
      const res = await deleteQuestion(deletedSlides.current);
      if (!res.status) {
        notify('Failed to delete slides.', 'error');
      } else {
        // Clear both the ref and Dexie on successful deletion
        updateDeletedSlides([]);
      }
    }
  };

  const getParsedSlides = () => {
    // 1. Handle read-only mode
    if (!isEditable) {
      return slides.map((slide) => ({ ...slide, id: undefined }));
    }
    let isDuplicatesRemoved = false;

    // 2. Filter out deleted slides
    const filteredSlides = slides.filter((slide) => {
      if (slide.id && deletedSlides.current.includes(slide.id)) {
        return false;
      }
      return true;
    });

    // 3. Remove duplicate options
    const newSlides = filteredSlides.map((slide) => {
      const seen = new Set();
      if (Array.isArray(slide.options) && slide.options.length > 0) {
        slide.options = slide.options.filter((option) => {
          if (seen.has(option.body)) {
            isDuplicatesRemoved = true;
            return false;
          }
          seen.add(option.body);
          return true;
        });
      }
      return slide;
    });

    if (isDuplicatesRemoved) notify('Duplicate options were removed.', 'info');

    // Return the processed slides
    return newSlides;
  };

  const saveTemplate = async (customPayload?: any, isAutoSave = false) => {
    // Prevent auto-save during undo/redo operations
    if (isAutoSave && isUndoRedoInProgress.current) {
      return { status: false, message: 'Skipped - undo/redo in progress' };
    }

    try {
      presentation.isSaving();
      pushDataLayerForEvent('save_template');

      // Only delete pending slides during manual save, not auto-save
      // This preserves the ability to undo slide deletions
      if (!isAutoSave) {
        await deletePendingSlides();
      }

      if (session && selectedTheme) {
        await updateLiveStreamSettings(session.id, {
          settings: {
            ...session.settings,
            ...selectedTheme.config.textFontFamily,
            ...selectedTheme.config.images,
            ...selectedTheme.config.headingFontFamily,
          },
          brandColours: selectedTheme.config.colors
        });
      }

      // Store the original template ID before preparing payload
      const originalTemplateId = templateId;

      // Ensure title is never empty
      const finalTitle = (customPayload?.title || rosTitle || 'Untitled Presentation').trim();
      if (!finalTitle) {
        throw new Error('Title cannot be empty');
      }

      const payload = prepareROSPayload(
        {
          // Use original template ID for both auto-save and manual save
          id: originalTemplateId,
          title: finalTitle,
          createdBy: template?.created_by || auth.id,
          isEditable,
          layoutMode,
          selected_theme_id: selectedThemeId,
        },
        getParsedSlides(),
        teamContext?.oid,
        customPayload?.deletedSlides || deletedSlides.current, // Use provided deleted slides or current ones
        !originalTemplateId || !isEditable
      );

      // Preserve theme ID in custom payload if it exists
      const finalPayload = customPayload ? {
        ...customPayload,
        title: finalTitle, // Ensure title is set in custom payload
        selected_theme_id: customPayload.selected_theme_id || selectedThemeId,
      } : payload;

      const res = await createRosTemplate(finalPayload);

      if (res.status) {
        // For auto-save, don't update slides from server to preserve local state and undo stack
        if (!isAutoSave) {
          // Filter out any slides that are still in our deletedSlides list
          // This prevents deleted slides from reappearing if the server still has them
          const filteredSlides = res.entity.questions.filter((slide: Interaction) => {
            return !slide.id || !deletedSlides.current.includes(slide.id);
          });

          setSlides(filteredSlides);

          // Don't clear deleted slides here - it's already handled in deletePendingSlides
        } else {
          // For auto-save, only update IDs for new slides without IDs
          const serverSlides = res.entity.questions;
          let needsUpdate = false;
          const updatedSlides = slides.map((slide, index) => {
            if (!slide.id || !slide.link_id) {
              needsUpdate = true;
              return {
                ...slide,
                id: serverSlides[index]?.id || slide.id,
                link_id: serverSlides[index]?.link_id || slide.link_id
              };
            }
            return slide;
          });

          if (needsUpdate) {
            setSlides(updatedSlides);
          }
        }

        // Ensure theme ID is preserved in response entity
        const updatedEntity = {
          ...res.entity,
          selected_theme_id: selectedThemeId
        };
        setTemplate(updatedEntity);

        setRosTitle(res.entity.title);
        setIsEditable(res.entity.editable);

        // Update template ID and handle URL updates
        const isNewPresentation = !originalTemplateId;

        if (isNewPresentation) {
          // For new presentations, update the URL first with replace to avoid adding to history
          const newUrl = `/create-presentation?templateId=${res.entity.id}`;
          const currentUrl = router.asPath;

          const presentationInfo = await getPresentationInfo();

          if (presentationInfo) {
            const combinedTemplateId = `${res.entity.id}_${presentationInfo.title}`;
            localStorage.setItem('template_id', combinedTemplateId);
          } else if (presentationInfoError) {
            console.error('Error:', presentationInfoError);
          }

          // Only update URL if it's different to prevent unnecessary navigation
          if (!currentUrl.includes(`templateId=${res.entity.id}`)) {
            router.replace(newUrl, undefined, { shallow: true });
          }

          // Then update the template ID
          setTemplateId(res.entity.id);
        } else if (!isAutoSave) {
          // For existing presentations, only update template ID if not auto-saving
          setTemplateId(res.entity.id);
        }

        pushDataLayerForEvent('update ros template', { templateId });
        if (selectedThemeId && template?.selected_theme_id !== selectedThemeId) {
          await applySelectedThemeApi(selectedThemeId, res.entity.id);
        }
        // if (!customPayload && !isAutoSave) notify(res.message, 'success');

        presentation.isClean();
        // Mark save point after auto-save to prevent undo past this point
        if (isAutoSave) {
          // For auto-save, DON'T update savePointIndex - allow undo past auto-saved changes
          // Update button states - can undo if there are items in stack
          const canUndoResult = undoStack.current.length > 0;
          const canRedoResult = redoStack.current.length > 0;
          setCanUndo(canUndoResult);
          setCanRedo(canRedoResult);
        } else {
          // Manual save - reset the flag and update button states
          hasUndoClearedAfterSave.current = false;
          savePointIndex.current = undoStack.current.length;
          setCanUndo(undoStack.current.length > 0);
          setCanRedo(redoStack.current.length > 0);
        }

        if (!isEditable || (!session && !templateId) || !!customPayload) {
          router.replace(
            `/create-presentation?templateId=${res.entity.id}`,
            undefined,
            {
              shallow: true
            }
          );
        }
      } else {
        const errorMsg = 'Something went wrong!';
        notify(res.message?.template?.[0] || errorMsg, 'error');
        presentation.isDirty();
      }
      return res;
    } finally {
      presentation.isClean();
    }
  };

  const duplicateTemplate = async () => {
    let newSlides = slides.map(({ link_id, ...rest }) => rest);

    // Create template copy with theme
    const templateCopy = {
      id: undefined,
      title: rosTitle || 'Untitled presentation',
      createdBy: auth.id,
      isEditable,
      selected_theme_id: selectedThemeId,
      layout_mode: layoutMode
    };

    const payload = prepareROSPayload(
      templateCopy,
      newSlides,
      teamContext?.oid,
      [],
      true
    );

    const res = await saveTemplate(payload);
    if (res.status) {
      // First apply the theme to the new template
      if (selectedThemeId) {
        await applySelectedThemeApi(selectedThemeId, res.entity.id);
      }

      // Then select the presentation with the updated theme
      const updatedTemplate = {
        ...res.entity,
        selected_theme_id: selectedThemeId
      };
      await selectPresentation(updatedTemplate);
      notify('Presentation successfully duplicated.');
    }
  };

  const deleteTemplate = async () => {
    // setLoading(true);
    if (!isEditable) return notify('This template cannot be deleted.', 'error');
    const deletedTemp = await deleteRosTemplate(templateId);
    if (deletedTemp.status) {
      // Clear backup after successful deletion
      if (templateId) {
        try {
          await db.deletePresentation(templateId);
          console.log('Successfully cleared backup for template:', templateId);
        } catch (error) {
          console.error('Failed to clear backup:', error);
        }
      }
      notify(deletedTemp.message);
      router.push('/presentations');
    }
  };

  const [isPasting, setIsPasting] = useState(false);
  const [pastedRange, setPastedRange] = useState<{ start: number; end: number } | null>(null);
  const lastPastedIndexRef = useRef<number | null>(null);
  const pasteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pastedRangeRef = useRef<{ start: number; end: number } | null>(null);
  const lastPasteTimeRef = useRef<number>(0);
  const lastSlideCreationTimeRef = useRef<number>(0);
  const lastWarningTimeRef = useRef<number>(0);
  const PASTE_COOLDOWN = 2000; // 2 second cooldown between pastes
  const SLIDE_CREATION_COOLDOWN = 500; // 0.5 second cooldown between slide creations
  const WARNING_COOLDOWN = 2000; // 2 second cooldown between warnings
  const pasteCountRef = useRef<number>(0);
  const pasteCountResetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetPasteCount = () => {
    pasteCountRef.current = 0;
    if (pasteCountResetTimeoutRef.current) {
      clearTimeout(pasteCountResetTimeoutRef.current);
    }
  };

  const showRateLimitWarning = (action: string) => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current < WARNING_COOLDOWN) return;

    lastWarningTimeRef.current = now;
    notify(`Please wait a moment before ${action} again.`, 'warning', {
      className: 'text-grey-900'
    });
  };

  const addNewSlide = (
    interactionType: InteractionType,
    slide?: Interaction
  ) => {
    // Removed rate limit check from here
    // Removed pushToHistory() since we don't want undo for new slides
    let options: InteractionOptions[] = [];
    options = getDefaultOptions(interactionType);

    // Get theme settings first
    const setting = getGlobalSettings(interactionType);
    const color = getGlobalColors(interactionType);

    // Check if all existing slides have the same transition type
    const existingSlides = slides.filter(s => s.transition_type !== undefined && s.transition_type !== '');
    const commonTransitionType = existingSlides.length > 0 &&
      existingSlides.every(s => s.transition_type === existingSlides[0].transition_type)
      ? existingSlides[0].transition_type
      : 'none';

    if (slide) {
      // Apply theme settings to the slide being added
      slide.setting = { ...setting, ...(slide.setting || {}) };
      slide.color = { ...color, ...(slide.color || {}) };

      if (['pollWithOptions', 'wheel', 'multipleChoice'].includes(interactionType) && slide.options?.length < 2) {
        slide.options = [...(slide.options || []), ...options];
      }
    }

    updateSelectedSlideIndex(selectedSlideIndex + 1);

    setSlides((prev) => {
      const changedSlide = [...prev];
      const updatedSlide = slide || {
        ...defaultInteractionConfig,
        title: getDefaultTitle(interactionType),
        setting: { ...setting },
        color: { ...color },
        type: interactionType,
        options: [...options],
        transition_type: commonTransitionType, // Apply common transition type to new slide
      };
      changedSlide.splice(selectedSlideIndex + 1, 0, updatedSlide);
      return changedSlide;
    });
    setSelectedPanelBtn(slide ? '' : panelBtnEnum.content);
    presentation.isDirty();
  };

  const deleteSlide = (index: number) => {
    if (slides.length === 1) {
      notify('Cannot delete all the slides. Please keep at least one slide.', 'error');
      return;
    }
    const slideToDelete = slides[index];
    if (slideToDelete?.id && isEditable && !deletedSlides.current.includes(slideToDelete.id)) {
      // Use the helper function to update both the ref and Dexie
      updateDeletedSlides([...deletedSlides.current, slideToDelete.id]);
    }
    setSlides((prev) => prev.filter((_, i) => index !== i));
    let newIndex: number;
    if (slides.length === 1) newIndex = -1;
    else newIndex = Math.max(0, index - 1);
    setSelectedSlideIndex(newIndex);
    setSelectedPanelBtn(
      ['PDF Slide', 'connectSlide'].includes(slides[newIndex]?.type) ? '' : panelBtnEnum.content
    );

    presentation.isDirty();
    if (isAutoSaveEnabled && !isUndoRedoInProgress.current) {
      saveTemplate();
    }
  };

  const batchDeleteSlides = (indices: number[]) => {
    if (slides.length === indices.length) {
      notify('Cannot delete all the slides. Please keep at least one slide.', 'error');
      return;
    }

    // Sort indices in descending order to delete from end to start
    const sortedIndices = [...indices].sort((a, b) => b - a);

    // Track slides to delete
    const slidesToDelete = sortedIndices.map(index => slides[index]);

    // Update deletedSlides for all slides at once
    const newDeletedSlides = [...deletedSlides.current];
    slidesToDelete.forEach(slide => {
      if (slide?.id && isEditable && !newDeletedSlides.includes(slide.id)) {
        newDeletedSlides.push(slide.id);
      }
    });
    updateDeletedSlides(newDeletedSlides);

    // Remove all slides at once
    setSlides(prev => prev.filter((_, i) => !indices.includes(i)));

    // Update selected index
    const newIndex = Math.max(0, Math.min(...indices) - 1);
    setSelectedSlideIndex(newIndex);
    setSelectedPanelBtn(['PDF Slide', 'connectSlide'].includes(slides[newIndex]?.type) ? '' : panelBtnEnum.content);

    // Trigger auto-save after batch delete
    presentation.isDirty();
    if (isAutoSaveEnabled && !isUndoRedoInProgress.current) {
      saveTemplate();
    }
  };

  const [isDuplicating, setIsDuplicating] = useState(false);
  const duplicateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const DUPLICATE_COOLDOWN = 2000; // 2 seconds cooldown

  const duplicateSlide = async () => {
    const now = Date.now();
    if (isDuplicating || now - lastSlideCreationTimeRef.current < DUPLICATE_COOLDOWN) {
      showRateLimitWarning('duplicating');
      return;
    }
    lastSlideCreationTimeRef.current = now;
    setIsDuplicating(true);

    if (duplicateTimeoutRef.current) {
      clearTimeout(duplicateTimeoutRef.current);
    }
    duplicateTimeoutRef.current = setTimeout(() => {
      setIsDuplicating(false);
    }, DUPLICATE_COOLDOWN);

    const selectedIds = Object.keys(selectedSlideIds);
    const newSlides = [...slides];

    if (selectedIds.length > 0) {
      const selectedIndices = selectedIds
        .map(id => newSlides.findIndex(slide => slide.id === id))
        .filter(index => index !== -1)
        .sort((a, b) => a - b);

      const duplicates = selectedIndices.map(index => ({
        ...newSlides[index],
        options: newSlides[index]?.options?.map(option => ({
          ...option,
          id: undefined
        })) || [],
        id: undefined, // Remove ID to let server generate new one
        link_id: undefined, // Remove link_id to let server generate new one
        title: newSlides[index].title,
      }));

      // Insert all duplicates after the last selected slide
      const lastSelectedIndex = Math.max(...selectedIndices);
      newSlides.splice(lastSelectedIndex + 1, 0, ...duplicates);

      // Clear selected IDs since we're waiting for server response
      setSelectedSlideIds({});

      // Set the last duplicated slide as the main selected slide
      const lastDuplicatedIndex = lastSelectedIndex + duplicates.length;
      setSelectedSlideIndex(lastDuplicatedIndex);
    } else {
      const selectedSlide = newSlides[selectedSlideIndex];
      const newSlide = {
        ...selectedSlide,
        id: undefined, // Remove ID to let server generate new one
        link_id: undefined, // Remove link_id to let server generate new one
        title: selectedSlide.title,
      };
      newSlides.splice(selectedSlideIndex + 1, 0, newSlide);
      setSelectedSlideIndex(selectedSlideIndex + 1);
    }

    setSlides(newSlides);
    presentation.isDirty();
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (duplicateTimeoutRef.current) {
        clearTimeout(duplicateTimeoutRef.current);
      }
    };
  }, []);

  const onCopyInteractionLink = (index: number) => {
    const question = slides[index];
    if (question.original_question_id) {
      setSlides((prev) => {
        const changedSlide = [...prev];
        changedSlide[selectedSlideIndex].original_question_id = null;
        return changedSlide;
      });
    }
    const link_id = question.link_id;
    if (!link_id) return notify('Link not found.', 'error');
    const fullPath = `${process.env.NEXT_PUBLIC_MAIN_APP_URL}/l1/${link_id}?layoutMode=${layoutMode}`;
    navigator.clipboard.writeText(fullPath);
    notify('Link copied to clipboard.');
  };

  const onCopyInteraction = (index: number) => {
    // Check if we have multiple slides selected
    const selectedIds = Object.keys(selectedSlideIds);
    if (selectedIds.length > 0) {
      // Get all selected slides
      const selectedSlides = selectedIds
        .map(id => slides.find(slide => slide.id === id))
        .filter(slide => slide !== undefined);

      // Copy all selected slides as an array
      navigator.clipboard.writeText(JSON.stringify(selectedSlides));
      return;
    }

    // Single slide copy
    const question = slides[index];
    if (question === undefined) return;

    if (question.original_question_id) {
      setSlides((prev) => {
        const changedSlide = [...prev];
        changedSlide[selectedSlideIndex].original_question_id = null;
        return changedSlide;
      });
    }
    navigator.clipboard.writeText(JSON.stringify(question));
  };

  const onPasteInteraction = async (index: number) => {
    const now = Date.now();
    // Check if we're already pasting
    if (isPasting) {
      showRateLimitWarning('pasting');
      return;
    }
    // Check cooldown period
    if (now - lastPasteTimeRef.current < PASTE_COOLDOWN) {
      showRateLimitWarning('pasting');
      return;
    }
    lastPasteTimeRef.current = now;
    setIsPasting(true);

    // Clear any existing timeout
    if (pasteTimeoutRef.current) {
      clearTimeout(pasteTimeoutRef.current);
    }

    try {
      const clipText = await navigator.clipboard.readText();
      const clipData = JSON.parse(clipText);

      if (Array.isArray(clipData)) {
        // Flatten in case of nested arrays
        const flatSlides = clipData.flat();
        const itemsToPaste = flatSlides.map(item => {
          const cleanItem = cloneDeep(item);
          delete cleanItem.id;
          delete cleanItem.link_id;
          delete cleanItem.original_question_id;
          // Ensure color is preserved if present, fallback to original or default
          if (!cleanItem.color) {
            cleanItem.color = item.color || defaultInteractionConfig.color;
          }
          // Ensure transition_type is set, fallback to 'none'
          if (!cleanItem.transition_type) {
            cleanItem.transition_type = 'none';
          }
          return cleanItem;
        });

        setSlides((prev) => {
          const newSlides = [
            ...prev.slice(0, index + 1),
            ...itemsToPaste,
            ...prev.slice(index + 1),
          ];
          const firstPastedIndex = index + 1;
          const lastPastedIndex = index + itemsToPaste.length;
          // Select all newly pasted slides
          const pastedIds = newSlides.slice(firstPastedIndex, lastPastedIndex + 1)
            .map(slide => slide.id)
            .filter((id): id is string => !!id);
          setSelectedSlideIndex(lastPastedIndex);
          pastedRangeRef.current = { start: firstPastedIndex, end: lastPastedIndex };
          setPastedRange({ start: firstPastedIndex, end: lastPastedIndex });
          lastPastedIndexRef.current = lastPastedIndex;
          // Optionally update selectedSlideIds here if needed
          return newSlides;
        });

        pasteTimeoutRef.current = setTimeout(() => {
          setSelectedSlideIndex(index + itemsToPaste.length);
        }, 0);
      } else {
        const itemToDuplicate = cloneDeep(clipData);
        delete itemToDuplicate.id;
        delete itemToDuplicate.link_id;
        delete itemToDuplicate.original_question_id;
        // Ensure transition_type is set, fallback to 'none'
        if (!itemToDuplicate.transition_type) {
          itemToDuplicate.transition_type = 'none';
        }

        setSlides((prev) => {
          const newSlides = [
            ...prev.slice(0, index + 1),
            itemToDuplicate,
            ...prev.slice(index + 1),
          ];
          const pastedIndex = index + 1;
          setSelectedSlideIndex(pastedIndex);
          pastedRangeRef.current = { start: pastedIndex, end: pastedIndex };
          return newSlides;
        });

        const pastedIndex = index + 1;
        setPastedRange({ start: pastedIndex, end: pastedIndex });
        lastPastedIndexRef.current = pastedIndex;

        pasteTimeoutRef.current = setTimeout(() => {
          setSelectedSlideIndex(pastedIndex);
        }, 0);
      }

      setSelectedPanelBtn(
        Array.isArray(clipData) && ['PDF Slide', 'connectSlide'].includes(clipData[0]?.type)
          ? ''
          : panelBtnEnum.content
      );
      presentation.isDirty();
    } catch (err) {
      console.error('Failed to paste interaction:', err);
      notify('Failed to paste interaction from clipboard.', 'error');
    } finally {
      setIsPasting(false);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (pasteTimeoutRef.current) {
        clearTimeout(pasteTimeoutRef.current);
      }
    };
  }, []);

  /**
   * When you change the interaction type from the content panel
   */
  const changeInteraction = (value: string | number) => {
    pushToHistory();
    setSlides((prev) => {
      const existingTitle = prev[selectedSlideIndex].title;
      const existingDefaultTitle = getDefaultTitle(prev[selectedSlideIndex].type as InteractionType);
      const wasCustomTitle = existingTitle !== existingDefaultTitle;

      // Get the current theme's colors and settings
      const themeToApply = allThemes.find(theme => theme.id === selectedThemeId);
      const themeColors = themeToApply?.config?.colors || {};
      const themeConfig = themeToApply?.config as ThemeConfig || {
        textFontFamily: {},
        headingFontFamily: {},
        images: {},
        default: {},
        settings: {},
        colors: {}
      };

      let slide: Interaction = {
        ...defaultInteractionConfig,
        title: wasCustomTitle ? existingTitle : getDefaultTitle(value as InteractionType),
        setting: {
          ...getGlobalSettings(value as InteractionType)
        },
        color: {
          ...getGlobalColors(value as InteractionType),
          ...themeColors // Apply theme colors
        },
        transition_type: prev[selectedSlideIndex].transition_type || 'none', // Preserve existing transition type
      };

      // Apply theme-specific settings based on the new slide type
      if (value === 'pollWithOptions') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.pollWithOptionsFontStyle;
        slide.setting.pollWithOptionsImageSrc = themeConfig.images?.pollWithOptionsImageSrc;
        slide.setting.pollWithOptionsImageOpacity = themeConfig.images?.pollWithOptionsImageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.pollWithOptionsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'freeForm') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.pollFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.pollImageSrc = themeConfig.images?.pollImageSrc || themeConfig.default?.imageSrc;
        slide.setting.pollImageOpacity = themeConfig.images?.pollImageOpacity || themeConfig.default?.imageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.pollHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'fantasticFans') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.fantasticFansFontStyle;
        slide.setting.ffImageSrc = themeConfig.images?.ffImageSrc;
        slide.setting.ffImageOpacity = themeConfig.images?.ffImageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.fantasticFansHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'textTrack') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.textTrackFontStyle;
        slide.setting.textTrackImageSrc = themeConfig.images?.textTrackImageSrc;
        slide.setting.textTrackImageOpacity = themeConfig.images?.textTrackImageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.textTrackHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'transientThought') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.transientThoughtFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.ttImageSrc = themeConfig.images?.ttImageSrc;
        slide.setting.ttImageOpacity = themeConfig.images?.ttImageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.transientThoughtHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'wordCloud') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.wordCloudFontStyle;
        slide.setting.wcImageSrc = themeConfig.images?.wcImageSrc;
        slide.setting.wcImageOpacity = themeConfig.images?.wcImageOpacity;
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.wordCloudHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
      } else if (value === 'emojisEverywhere') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.emojisEverywhereHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.emojisEverywhereImageSrc = themeConfig.images?.emojisEverywhereImageSrc;
        slide.setting.emojisEverywhereImageOpacity = themeConfig.images?.emojisEverywhereImageOpacity;
      } else if (value === 'map') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.mapHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.mapImageSrc = themeConfig.images?.mapImageSrc;
        slide.setting.mapImageOpacity = themeConfig.images?.mapImageOpacity;
        slide.setting.mapConfig = themeConfig.settings?.mapConfig;
      } else if (value === 'wheel') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.wheelHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.choiceCircleImageSrc = themeConfig.images?.choiceCircleImageSrc;
        slide.setting.choiceCircleImageOpacity = themeConfig.images?.choiceCircleImageOpacity;
      } else if (value === 'talkingTiles') {
        slide.setting.FontStyle = themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.talkingTilesHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.talkingTilesImageSrc = themeConfig.images?.talkingTilesImageSrc;
        slide.setting.talkingTilesImageOpacity = themeConfig.images?.talkingTilesImageOpacity;
      } else if (value === 'newWheel') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.newWheelHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.winningWheelImageSrc = themeConfig.images?.winningWheelImageSrc;
        slide.setting.winningWheelImageOpacity = themeConfig.images?.winningWheelImageOpacity;
      } else if (value === 'linkLibrary') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.linkLibraryHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.linkLibraryImageSrc = themeConfig.images?.linkLibraryImageSrc;
        slide.setting.linkLibraryImageOpacity = themeConfig.images?.linkLibraryImageOpacity;
      } else if (value === 'Quick Questions') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.quickQuestionsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.quickQuestionImageSrc = themeConfig.images?.quickQuestionImageSrc;
        slide.setting.quickQuestionImageOpacity = themeConfig.images?.quickQuestionImageOpacity;
      } else if (value === 'ratingPolls') {
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.ratingPollsHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.ratingPollsImageSrc = themeConfig.images?.ratingPollsImageSrc;
        slide.setting.ratingPollsImageOpacity = themeConfig.images?.ratingPollsImageOpacity;
      } else if (value === 'multipleChoice') {
        slide.setting.FontStyle = themeConfig.textFontFamily?.multipleChoiceFontStyle || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.HeadingFont = themeConfig.headingFontFamily?.multipleChoiceHeadingFont || themeConfig.default?.headingFontFamily || 'Sans-Serif';
        slide.setting.multipleChoiceImageSrc = themeConfig.images?.multipleChoiceImageSrc || themeConfig.default?.imageSrc;
        slide.setting.multipleChoiceImageOpacity = themeConfig.images?.multipleChoiceImageOpacity || themeConfig.default?.imageOpacity;
      }

      slide.options = getDefaultOptions(value as InteractionType);

      const slideExists = slideHistory.sync(
        selectedSlideIndex,
        prev[selectedSlideIndex],
        value as InteractionType
      );
      if (slideExists) slide = { ...slideExists };

      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex] = {
        ...slide,
        id: prev[selectedSlideIndex].id,
        type: value as InteractionType,
        title: wasCustomTitle ? existingTitle : slide.title // Only keep existing title if it was custom
      };
      return changedSlide;
    });
    presentation.isDirty();
  };

  /**
   * When you switch slides from the sidebar
   * @param {number} index Slide index
   */
  const switchSlide = (slide: Interaction, index: number) => () => {
    setSelectedSlideIndex(index);
    if (['PDF Slide', 'connectSlide'].includes(slide?.type)) setSelectedPanelBtn('');
    else setSelectedPanelBtn(panelBtnEnum.content);
  };

  const updateOptions = (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
    choiceCircleOptions: InteractionOptions[] = []
  ) => {
    // Only push to history immediately for non-text changes (like drag/drop options)
    if (!e) {
      pushToHistory('slide');
    }

    setSlides((prev) => {
      if (selectedSlideIndex === null) return prev;
      const changedSlide = [...prev];
      if (typeof index === 'number' && e?.target?.value !== undefined) {
        changedSlide[selectedSlideIndex].options[index].body = e.target.value;
      } else if (Array.isArray(choiceCircleOptions)) {
        changedSlide[selectedSlideIndex].options = choiceCircleOptions;
      }
      return changedSlide;
    });

    if (e) {
      if (optionChangeDelayRef.current) {
        clearTimeout(optionChangeDelayRef.current);
      }
      optionChangeDelayRef.current = setTimeout(() => {
        pushToHistory('slide');
        presentation.isDirty();
      }, 1000);
    } else {
      presentation.isDirty();
    }
  };

  const updateOptionsByName = (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e) {
      if (optionChangeDelayRef.current) {
        clearTimeout(optionChangeDelayRef.current);
      }
      optionChangeDelayRef.current = setTimeout(() => {
        pushToHistory('slide');
        presentation.isDirty();
      }, 1000);
    } else {
      pushToHistory();
      presentation.isDirty();
    }

    setSlides((prev) => {
      if (selectedSlideIndex === null) return prev;
      const changedSlides = [...prev];

      if (typeof index === 'number' && e?.target?.name && e?.target?.value !== undefined) {
        const optionToUpdate = cloneDeep(changedSlides[selectedSlideIndex]?.options[index]);
        if (!optionToUpdate) return changedSlides;

        const key = e.target.name;
        if (key === 'id' || key === 'body' || key === 'checked') {
          (optionToUpdate as any)[key] = e.target.value;
        }

        changedSlides[selectedSlideIndex].options = [
          ...changedSlides[selectedSlideIndex].options.slice(0, index),
          optionToUpdate,
          ...changedSlides[selectedSlideIndex].options.slice(index + 1),
        ];
      }

      return changedSlides;
    });
  };

  const addOption = () => {
    pushToHistory('slide');
    setSlides((prev) => {
      if (selectedSlideIndex === null) return prev;
      const changedSlide = [...prev];
      const newIndex = changedSlide[selectedSlideIndex].options.length;

      changedSlide[selectedSlideIndex].options.push({
        id: v4(),
        body: `Option ${newIndex + 1}`
      });
      return changedSlide;
    });
    presentation.isDirty();
  };

  const addBulkOptions = (count: number) => {
    pushToHistory('slide');
    setSlides((prev) => {
      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex].options = Array.from({ length: count }, (_, index) => ({ id: v4(), body: (index + 1).toString() }));
      return changedSlide;
    });
    presentation.isDirty();
  };

  const deleteOption = (index: number) => {
    pushToHistory('slide');
    setSlides((prev) => {
      if (selectedSlideIndex === null) return prev;
      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex].options.splice(index, 1);
      return [...changedSlide];
    });
    presentation.isDirty();
  };

  const updateTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target) return; // Only check if event and target exist
    const { value } = e.target;

    // Guard against pasting JSON object
    if (value.includes('{"id":')) { return; }

    setSlides((prev) => {
      if (selectedSlideIndex === null || selectedSlideIndex < 0) return prev;
      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex].title = value;
      return changedSlide;
    });

    if (titleChangeDelayRef.current) {
      clearTimeout(titleChangeDelayRef.current);
    }
    titleChangeDelayRef.current = setTimeout(() => {
      pushToHistory();
      presentation.isDirty();
    }, 1000);
  };

  const updateSelectedSlideIndex = (index: number) =>
    setSelectedSlideIndex(index);

  const updateSetting = (
    key: keyof InteractionSettings,
    value: any
  ) => {
    pushToHistory();
    setSlides((prev) => {
      const changedSlide = [...prev];
      let data: any;
      switch (key) {
        case 'DisplayStyle': {
          data = !value ? 'noOfVotes' : 'percentage';
          break;
        }
        case 'transientThoughtDisplayStyle': {
          data = value ? 'bubbles' : 'cloud';
          break;
        }
        case 'OptionsList': {
          data = !value ? 'character' : 'number';
          break;
        }
        case 'CombineWords':
        case 'CommentsOverlay':
        case 'FontStyle':
        case 'ProfanityFilter':
        case 'SplitWords':
        case 'Vertical':
        case 'oneUoneV':
        case 'showLatestCommentsFirst':
        case 'showUserName':
        case 'ttPlaybackSpeed':
        case 'wheelShowUser':
        case 'DefaultView':
        case 'transientThoughtShowUsername':
        case 'LinkType':
        default:
          data = value;
      }
      changedSlide[selectedSlideIndex] = {
        ...prev[selectedSlideIndex],
        setting: {
          ...prev[selectedSlideIndex].setting,
          [key]: data
        }
      };
      return changedSlide;
    });
    presentation.isDirty();
  };

  const updateColor = (
    key: keyof InteractionColors,
    value: string | boolean
  ) => {
    pushToHistory();
    setSlides((prev) => {
      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex] = {
        ...prev[selectedSlideIndex],
        color: {
          ...prev[selectedSlideIndex].color,
          [key]: value
        }
      };
      return changedSlide;
    });
    presentation.isDirty();
  };

  const updateTheme = (theme: Theme) => {
    pushToHistory('theme'); // Mark this as a theme-only change
    setSelectedThemeId(theme.id);
    setSlides((prev) => prev.map(slide => {
      if (['PDF Slide', 'connectSlide'].includes(slide?.type)) return slide;
      const newSlide = { ...slide };
      const colors = theme.config.colors;
      let FontStyle = slide.setting.FontStyle;

      if (slide.type === 'pollWithOptions') {
        FontStyle = theme.config.textFontFamily.pollWithOptionsFontStyle;
        newSlide.setting = {
          ...newSlide.setting,
          pollWithOptionsImageSrc: theme.config.images.pollWithOptionsImageSrc,
          pollWithOptionsImageOpacity: theme.config.images.pollWithOptionsImageOpacity,
          HeadingFont: theme.config.headingFontFamily.pollWithOptionsHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          pollWithOptionsFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'freeForm') {
        FontStyle = theme.config.textFontFamily.pollFontStyle || theme.config.default.headingFontFamily || 'Sans-Serif';
        newSlide.setting = {
          ...newSlide.setting,
          pollImageSrc: theme.config.images.pollImageSrc || theme.config.default.imageSrc,
          pollImageOpacity: theme.config.images.pollImageOpacity || theme.config.default.imageOpacity,
          HeadingFont: theme.config.headingFontFamily.pollHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          pollFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'fantasticFans') {
        FontStyle = theme.config.textFontFamily.fantasticFansFontStyle;
        newSlide.setting = {
          ...newSlide.setting,
          ffImageSrc: theme.config.images.ffImageSrc,
          ffImageOpacity: theme.config.images.ffImageOpacity,
          HeadingFont: theme.config.headingFontFamily.fantasticFansHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          ffFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'textTrack') {
        FontStyle = theme.config.textFontFamily.textTrackFontStyle;
        newSlide.setting = {
          ...newSlide.setting,
          textTrackImageSrc: theme.config.images.textTrackImageSrc,
          textTrackImageOpacity: theme.config.images.textTrackImageOpacity,
          HeadingFont: theme.config.headingFontFamily.textTrackHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          textTrackFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'transientThought') {
        FontStyle = theme.config.textFontFamily.transientThoughtFontStyle || theme.config.default.headingFontFamily || 'Sans-Serif';
        newSlide.setting = {
          ...newSlide.setting,
          ttImageSrc: theme.config.images.ttImageSrc,
          ttImageOpacity: theme.config.images.ttImageOpacity,
          HeadingFont: theme.config.headingFontFamily.transientThoughtHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif'
        };
      }
      if (slide.type === 'wordCloud') {
        FontStyle = theme.config.textFontFamily.wordCloudFontStyle;
        newSlide.setting = {
          ...newSlide.setting,
          wcImageSrc: theme.config.images.wcImageSrc,
          wcImageOpacity: theme.config.images.wcImageOpacity,
          HeadingFont: theme.config.headingFontFamily.wordCloudHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif'
        };
      }
      if (slide.type === 'emojisEverywhere') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.emojisEverywhereHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          emojisEverywhereImageSrc: theme.config.images.emojisEverywhereImageSrc,
          emojisEverywhereImageOpacity: theme.config.images.emojisEverywhereImageOpacity
        };
      }
      if (slide.type === 'map') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.mapHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          mapImageSrc: theme.config.images.mapImageSrc,
          mapImageOpacity: theme.config.images.mapImageOpacity,
          mapConfig: theme.config.settings?.mapConfig
        };
      }
      if (slide.type === 'wheel') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.wheelHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          choiceCircleImageSrc: theme.config.images.choiceCircleImageSrc,
          choiceCircleImageOpacity: theme.config.images.choiceCircleImageOpacity,
          choiceCircleFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'talkingTiles') {
        FontStyle = theme.config.default.headingFontFamily || 'Sans-Serif';
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.talkingTilesHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          talkingTilesImageSrc: theme.config.images.talkingTilesImageSrc,
          talkingTilesImageOpacity: theme.config.images.talkingTilesImageOpacity,
          talkingTilesFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'newWheel') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.newWheelHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          winningWheelImageSrc: theme.config.images.winningWheelImageSrc,
          winningWheelImageOpacity: theme.config.images.winningWheelImageOpacity,
          winningWheelFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'linkLibrary') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.linkLibraryHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          linkLibraryImageSrc: theme.config.images.linkLibraryImageSrc,
          linkLibraryImageOpacity: theme.config.images.linkLibraryImageOpacity,
          linkLibraryFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'Quick Questions') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.quickQuestionsHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          quickQuestionImageSrc: theme.config.images.quickQuestionImageSrc,
          quickQuestionImageOpacity: theme.config.images.quickQuestionImageOpacity,
          quickQuestionFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'ratingPolls') {
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.ratingPollsHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          ratingPollsImageSrc: theme.config.images.ratingPollsImageSrc,
          ratingPollsImageOpacity: theme.config.images.ratingPollsImageOpacity,
          ratingPollsFontSize: theme.config.fontSize
        };
      }
      if (slide.type === 'multipleChoice') {
        FontStyle = theme.config.textFontFamily.multipleChoiceFontStyle || theme.config.default.headingFontFamily || 'Sans-Serif';
        newSlide.setting = {
          ...newSlide.setting,
          HeadingFont: theme.config.headingFontFamily.multipleChoiceHeadingFont || theme.config.default.headingFontFamily || 'Sans-Serif',
          multipleChoiceImageSrc: theme.config.images.multipleChoiceImageSrc || theme.config.default.imageSrc,
          multipleChoiceImageOpacity: theme.config.images.multipleChoiceImageOpacity || theme.config.default.imageOpacity,
          multipleChoiceFontSize: theme.config.fontSize
        };
      }

      return {
        ...newSlide,
        color: colors,
        setting: {
          ...newSlide.setting,
          FontStyle
        }
      };
    }));

    // Mark presentation as dirty to trigger auto-save
    presentation.isDirty();
  };

  const resetDesign = () => {
    setSlides((prev) => {
      const changedSlide = [...prev];
      const currentSlide = changedSlide[selectedSlideIndex];
      const slideType = currentSlide.type;

      // Get default colors and settings for this slide type
      const color: InteractionColors = {
        ...defaultInteractionConfig.color,
        ...getGlobalColors(slideType)
      };
      const defaultSettings = getGlobalSettings(slideType);
      const theme = userThemes?.find(theme => theme.id === selectedThemeId)?.config || defaultThemes?.find(theme => theme.id === selectedThemeId)?.config;

      if (!theme) {
        currentSlide.setting = {
          ...currentSlide.setting,
          FontStyle: defaultSettings.FontStyle || 'Sans-Serif',
          HeadingFont: 'Sans-Serif'
        };
        return changedSlide;
      }

      // Map slide type to font style property name in theme config
      const fontStyleMap: { [key in InteractionType]?: keyof typeof theme.textFontFamily } = {
        pollWithOptions: 'pollWithOptionsFontStyle',
        freeForm: 'pollFontStyle',
        wordCloud: 'wordCloudFontStyle',
        textTrack: 'textTrackFontStyle',
        transientThought: 'transientThoughtFontStyle',
        fantasticFans: 'fantasticFansFontStyle',
        multipleChoice: 'multipleChoiceFontStyle'
      } as const;

      // Map slide type to heading font property name
      const headingFontMap: { [key in InteractionType]?: keyof ThemeFontFamily } = {
        pollWithOptions: 'pollWithOptionsHeadingFont',
        freeForm: 'pollHeadingFont',
        wordCloud: 'wordCloudHeadingFont',
        map: 'mapHeadingFont',
        emojisEverywhere: 'emojisEverywhereHeadingFont',
        talkingTiles: 'talkingTilesHeadingFont',
        transientThought: 'transientThoughtHeadingFont',
        textTrack: 'textTrackHeadingFont',
        newWheel: 'newWheelHeadingFont',
        wheel: 'wheelHeadingFont',
        linkLibrary: 'linkLibraryHeadingFont',
        'Quick Questions': 'quickQuestionsHeadingFont',
        fantasticFans: 'fantasticFansHeadingFont',
        ratingPolls: 'ratingPollsHeadingFont',
        multipleChoice: 'multipleChoiceHeadingFont'
      } as const;

      // Reset all design-related settings
      currentSlide.color = color;

      // Get the font style from theme config if available, otherwise use default
      const fontStyleKey = fontStyleMap[slideType];
      const fontStyle = fontStyleKey && theme?.textFontFamily?.[fontStyleKey]
        ? theme.textFontFamily[fontStyleKey]
        : theme?.default?.headingFontFamily || 'Sans-Serif';

      // Get the heading font from theme config if available, otherwise use default
      const headingFontKey = headingFontMap[slideType];
      const headingFont = headingFontKey && theme?.headingFontFamily?.[headingFontKey]
        ? theme.headingFontFamily[headingFontKey]
        : theme?.default?.headingFontFamily || 'Sans-Serif';

      currentSlide.setting = {
        ...currentSlide.setting,
        FontStyle: fontStyle,
        HeadingFont: headingFont
      };

      // Reset background image and related settings based on slide type
      if (slideType === 'pollWithOptions') {
        currentSlide.setting = {
          ...currentSlide.setting,
          pollWithOptionsImageSrc: defaultSettings.pollWithOptionsImageSrc,
          pollWithOptionsImageOpacity: defaultSettings.pollWithOptionsImageOpacity
        };
      } else if (slideType === 'freeForm') {
        currentSlide.setting = {
          ...currentSlide.setting,
          pollImageSrc: defaultSettings.pollImageSrc,
          pollImageOpacity: defaultSettings.pollImageOpacity
        };
      } else if (slideType === 'wordCloud') {
        currentSlide.setting = {
          ...currentSlide.setting,
          wcImageSrc: defaultSettings.wcImageSrc,
          wcImageOpacity: defaultSettings.wcImageOpacity
        };
      } else if (slideType === 'map') {
        currentSlide.setting = {
          ...currentSlide.setting,
          mapImageSrc: defaultSettings.mapImageSrc,
          mapImageOpacity: defaultSettings.mapImageOpacity,
          mapConfig: defaultSettings.mapConfig
        };
      } else if (slideType === 'emojisEverywhere') {
        currentSlide.setting = {
          ...currentSlide.setting,
          emojisEverywhereImageSrc: defaultSettings.emojisEverywhereImageSrc,
          emojisEverywhereImageOpacity: defaultSettings.emojisEverywhereImageOpacity
        };
      } else if (slideType === 'talkingTiles') {
        currentSlide.setting = {
          ...currentSlide.setting,
          talkingTilesImageSrc: defaultSettings.talkingTilesImageSrc,
          talkingTilesImageOpacity: defaultSettings.talkingTilesImageOpacity
        };
      } else if (slideType === 'transientThought') {
        currentSlide.setting = {
          ...currentSlide.setting,
          ttImageSrc: defaultSettings.ttImageSrc,
          ttImageOpacity: defaultSettings.ttImageOpacity
        };
      } else if (slideType === 'textTrack') {
        currentSlide.setting = {
          ...currentSlide.setting,
          textTrackImageSrc: defaultSettings.textTrackImageSrc,
          textTrackImageOpacity: defaultSettings.textTrackImageOpacity
        };
      } else if (slideType === 'newWheel') {
        currentSlide.setting = {
          ...currentSlide.setting,
          winningWheelImageSrc: defaultSettings.winningWheelImageSrc,
          winningWheelImageOpacity: defaultSettings.winningWheelImageOpacity
        };
      } else if (slideType === 'linkLibrary') {
        currentSlide.setting = {
          ...currentSlide.setting,
          linkLibraryImageSrc: defaultSettings.linkLibraryImageSrc,
          linkLibraryImageOpacity: defaultSettings.linkLibraryImageOpacity
        };
      } else if (slideType === 'Quick Questions') {
        currentSlide.setting = {
          ...currentSlide.setting,
          quickQuestionImageSrc: defaultSettings.quickQuestionImageSrc,
          quickQuestionImageOpacity: defaultSettings.quickQuestionImageOpacity
        };
      } else if (slideType === 'ratingPolls') {
        currentSlide.setting = {
          ...currentSlide.setting,
          ratingPollsImageSrc: defaultSettings.ratingPollsImageSrc,
          ratingPollsImageOpacity: defaultSettings.ratingPollsImageOpacity
        };
      } else if (slideType === 'multipleChoice') {
        currentSlide.setting = {
          ...currentSlide.setting,
          multipleChoiceImageSrc: defaultSettings.multipleChoiceImageSrc,
          multipleChoiceImageOpacity: defaultSettings.multipleChoiceImageOpacity
        };
      }

      return changedSlide;
    });

    pushToHistory();
    presentation.isDirty();
    notify(
      'Your design changes on this slide have been undone and set back to default.'
    );
  };

  const updateLayoutMode = (mode: 'square' | 'rectangle') => {
    setLayoutMode(mode);
    presentation.isDirty();
  };

  const slideRef = useRef(slides);
  slideRef.current = slides;

  const handleMultiDnD = (sourceIndices: number[], targetIndex: number) => {
    // Log multi-DnD operation
    // console.debug('handleMultiDnD called:', { sourceIndices, targetIndex });

    // Save current state for undo
    pushToHistory('slide');

    setSlides(prevSlides => {
      // Get the slides to move
      const selectedSlides = sourceIndices.map(index => prevSlides[index]);

      // Remove slides from highest index to lowest to not affect other indices
      const newSlides = [...prevSlides];
      [...sourceIndices].sort((a, b) => b - a).forEach(index => {
        newSlides.splice(index, 1);
      });

      // Insert all slides at the target position
      newSlides.splice(targetIndex, 0, ...selectedSlides);

      // Update the selected index to follow the moved slides
      setSelectedSlideIndex(targetIndex);
      setSelectedPanelBtn(selectedSlides[0].type === 'PDF Slide' ? '' : 'Content');

      return newSlides;
    });
    presentation.isDirty();
  };

  const handleDnD = (oldIndex: number, newIndex: number) => {
    // Log DnD operation
    // console.debug('handleDnD called:', { oldIndex, newIndex });

    // Save current state for undo
    pushToHistory('slide');

    setSlides(prevSlides => {
      const newSlides = [...prevSlides];
      const [movedSlide] = newSlides.splice(oldIndex, 1);
      newSlides.splice(newIndex, 0, movedSlide);

      // Update the selected index to follow the moved slide
      setSelectedSlideIndex(newIndex);
      setSelectedPanelBtn(['PDF Slide', 'connectSlide'].includes(movedSlide.type) ? '' : 'Content');
      return newSlides;
    });
    presentation.isDirty();
  };

  const onOptionChange = (options: InteractionOptions[]) => {
    // Explicitly specify this is a slide change for undo/redo
    pushToHistory('slide');
    setSlides((prev) => {
      const changedSlide = [...prev];
      changedSlide[selectedSlideIndex].options = options;
      return changedSlide;
    });
    presentation.isDirty();
  };

  const applyToSimilarSlides = () => {
    // Explicitly specify this is a slide change for undo/redo
    pushToHistory('slide');

    const currentSlide = slides[selectedSlideIndex];
    setSlides((prev) => {
      return prev.map((slide) => {
        if (slide.type === currentSlide.type) {
          slide.color = { ...currentSlide.color };
          slide.setting.FontStyle = currentSlide.setting.FontStyle;
        }
        return slide;
      });
    });

    const interactionNameMap: Record<InteractionType, string> = {
      'PDF Slide': 'PDF Slide',
      wheel: 'choice circle',
      pollWithOptions: 'power polls',
      freeForm: 'open-ended polls',
      wordCloud: 'wonder words',
      map: 'magic maps',
      emojisEverywhere: 'emojis everywhere',
      talkingTiles: 'talking tiles',
      transientThought: 'transient thoughts',
      'Quick Questions': 'quick questions',
      linkLibrary: 'link library',
      textTrack: 'text track',
      newWheel: 'winning wheel',
      ratingPolls: 'rating polls',
      fantasticFans: 'fantastic fans',
      multipleChoice: 'multiple choice',
      connectSlide: 'connect slide',
    };
    notify(
      `Your design changes have been successfully applied to all ${interactionNameMap[currentSlide.type]}.`
    );

    presentation.isDirty();
  };

  const handler: TSlideHandlers = {
    applyToSimilarSlides,
    onOptionChange,
    onTitleChange: (value) => updateTitle({ target: { value } } as any),
    updateTitle,
    updateSetting,
    updateColor,
    addOption,
    updateOptions,
    deleteOption,
    resetDesign,
    updateOptionsByName,
    addBulkOptions

  };
  const [showGenerateWithAIModal, setShowGenerateWithAIModal] = useState(false);
  const [disableAutoFocus, setDisableAutoFocus] = useState(false);

  const selectedSlide = { ...slides[selectedSlideIndex], handler };

  const [selectedSlideIds, setSelectedSlideIds] = useState<Record<string, string>>({});

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent browser's default save dialog
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault();
        saveTemplate();
      }

      // Handle undo/redo shortcuts
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === 'z') {
        event.preventDefault();
        undo();
      }

      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'z') {
        event.preventDefault();
        redo();
      }

      // Alternative redo shortcut (Ctrl+Y)
      if ((event.metaKey || event.ctrlKey) && event.key === 'y') {
        event.preventDefault();
        redo();
      }

      // --- Slide navigation with up/down arrow keys ---
      // Only trigger if not focused on input/textarea/contenteditable
      if (!isActiveElementInputLike() && slides.length > 0) {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (selectedSlideIndex > 0) {
            setSelectedSlideIndex(selectedSlideIndex - 1);
            setDisableAutoFocus(true);
            const slide = slides[selectedSlideIndex - 1];
            if (slide?.type === 'PDF Slide') setSelectedPanelBtn('');
            else setSelectedPanelBtn(panelBtnEnum.content);
          }
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (selectedSlideIndex < slides.length - 1) {
            setSelectedSlideIndex(selectedSlideIndex + 1);
            setDisableAutoFocus(true);
            const slide = slides[selectedSlideIndex + 1];
            if (slide?.type === 'PDF Slide') setSelectedPanelBtn('');
            else setSelectedPanelBtn(panelBtnEnum.content);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [slides, selectedSlideIndex]); // Re-attach listener when slides or index change

  // Load deleted slides from Dexie on mount
  useEffect(() => {
    if (templateId) {
      const loadDeletedSlides = async () => {
        try {
          const backup = await db.getPresentation(templateId);
          if (backup?.deletedSlides) {
            deletedSlides.current = backup.deletedSlides;
          }
        } catch (e) {
          console.error('Failed to load deleted slides from Dexie:', e);
        }
      };
      loadDeletedSlides();
    }
  }, [templateId]);

  // Helper function to update deleted slides in Dexie
  const updateDeletedSlides = async (slides: string[]) => {
    deletedSlides.current = slides;
    if (templateId) {
      try {
        // Get current backup
        const backup = await db.getPresentation(templateId);
        if (backup) {
          // Update backup with new deleted slides
          await db.savePresentation({
            ...backup,
            deletedSlides: slides
          });
        } else {
          // Create new backup if none exists
          await db.savePresentation({
            id: templateId,
            slides: [],
            rosTitle: rosTitle || 'Untitled Presentation',
            layoutMode,
            teamOwnerId: teamContext?.oid,
            timestamp: new Date().toISOString(),
            version: '1.0',
            deletedSlides: slides
          });
        }
      } catch (e) {
        console.error('Failed to update deleted slides in Dexie:', e);
      }
    }
  };

  // Auto-save functionality
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const titleChangeDelayRef = useRef<NodeJS.Timeout | null>(null);
  const optionChangeDelayRef = useRef<NodeJS.Timeout | null>(null);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // If we have unsaved changes and auto-save is enabled, trigger save
      if (saveState === 'unsaved' && isAutoSaveEnabled && slides.length > 0) {
        saveTemplate(undefined, true);
      }
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [saveState, isAutoSaveEnabled, slides.length]);

  const { applySelectedThemeApi } = useThemesAPI();

  // Use the auto-save hook
  const {
    retryCount,
    lastLocalSave
  } = useAutoSave({
    saveState,
    isAutoSaveEnabled,
    isOffline,
    slides,
    saveTemplate,
    presentation,
    templateId,
    setTemplateId: (id: string | null) => setTemplateId(id),
    rosTitle,
    layoutMode,
    teamOwnerId: teamContext?.oid
  });

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (titleChangeDelayRef.current) {
        clearTimeout(titleChangeDelayRef.current);
      }
      if (optionChangeDelayRef.current) {
        clearTimeout(optionChangeDelayRef.current);
      }
      if (pushHistoryTimeoutRef.current) {
        clearTimeout(pushHistoryTimeoutRef.current);
      }
      if (undoRedoTimeoutRef.current) {
        clearTimeout(undoRedoTimeoutRef.current);
      }
    };
  }, []);

  // Effect to select newly pasted slides after IDs are available
  useEffect(() => {
    if (pastedRangeRef.current) {
      const { start, end } = pastedRangeRef.current;
      // Wait until all slides in the range have IDs
      const pastedSlides = slides.slice(start, end + 1);
      const allHaveIds = pastedSlides.every(slide => !!slide.id);
      if (allHaveIds && pastedSlides.length > 0) {
        const pastedIds = pastedSlides.map(slide => slide.id).filter((id): id is string => !!id);
        const newSelected = pastedIds.reduce<Record<string, string>>((acc, id) => {
          acc[id] = id;
          return acc;
        }, {});
        setSelectedSlideIds(newSelected);
        pastedRangeRef.current = null;
      }
    }
  }, [slides]);

  const applySelectedTransitionToAllSlides = () => {
    // Add to undo history before making changes
    pushToHistory('slide');

    const selectedTransition = slides[selectedSlideIndex]?.transition_type || '';
    setSlides(prev => {
      const updatedSlides = prev.map(slide => ({
        ...slide,
        transition_type: selectedTransition,
      }));

      // Save to IndexedDB for persistence
      if (templateId) {
        db.getPresentation(templateId).then(backup => {
          if (backup) {
            db.savePresentation({
              ...backup,
              slides: updatedSlides,
              timestamp: new Date().toISOString()
            });
          }
        }).catch(e => {
          console.error('Failed to save transition changes to IndexedDB:', e);
        });
      }

      return updatedSlides;
    });

    // Mark presentation as dirty after updating slides
    presentation.isDirty();
  };

  function ensureMinimumOptions(
    existing: InteractionOptions[],
    fallback: InteractionOptions[],
    min: number = 2
  ): InteractionOptions[] {
    const needed = min - existing.length;
    return needed > 0 ? [...existing, ...fallback.slice(0, needed)] : existing;
  }

  function prepareInteractionOptions(
    slideInfo: any,
    interactionType: string
  ): InteractionOptions[] {
    const needsMinimumOptions = ['pollWithOptions', 'wheel', 'multipleChoice'];
    const defaultOptions = getDefaultOptions(interactionType as InteractionType);

    let baseOptions: InteractionOptions[] = [];

    if (Array.isArray(slideInfo.interaction?.options)) {
      if (interactionType === 'multipleChoice') {
        baseOptions = slideInfo.interaction.options.map((option: any, index: number) => ({
          id: v4(),
          option_number: index + 1,
          body: option.answer_choice,
          checked: option.checked ?? false,
        }));
      } else {
        baseOptions = slideInfo.interaction?.options.map((option: any, index: number) => ({
          id: v4(),
          option_number: index + 1,
          body: option,
          checked: false,
        }));
      }
    }

    return needsMinimumOptions.includes(interactionType)
      ? ensureMinimumOptions(baseOptions, defaultOptions, 2)
      : baseOptions;
  }

  const addSlides = (powerPointSlides: PowerpointSlideInfo[]): Promise<void> => {
    const sortedPowerPointSlides = [...powerPointSlides].sort((a, b) => a.slide_number - b.slide_number);

    return new Promise((resolve) => {
      const newSlides: Interaction[] = [];
      const insertStartIndex = selectedSlideIndex + 1;

      const slidesWithTransition = slides.filter(
        (slide) => slide.transition_type !== undefined && slide.transition_type !== ''
      );

      const isUniformTransition = slidesWithTransition.length > 0 && slidesWithTransition.every(
        (slide) => slide.transition_type === slidesWithTransition[0].transition_type
      );

      const commonTransitionType = isUniformTransition
        ? slidesWithTransition[0].transition_type!
        : 'none';

      for (const slideInfo of sortedPowerPointSlides) {
        const interactionType = interactionTypeMap[slideInfo.interaction.type] || 'freeForm';
        const setting = getGlobalSettings(interactionType);
        const color = getGlobalColors(interactionType);
        const options = prepareInteractionOptions(slideInfo, interactionType);

        const interactionObj: Interaction = {
          title: slideInfo.interaction.title,
          setting,
          color,
          type: interactionType as InteractionType,
          options,
          transition_type: commonTransitionType,
        };

        newSlides.push(interactionObj);
      }

      setSlides((prevSlides) => {
        const updatedSlides = [...prevSlides];
        updatedSlides.splice(insertStartIndex, 0, ...newSlides);
        return updatedSlides;
      });

      updateSelectedSlideIndex(insertStartIndex);
      setSelectedPanelBtn(panelBtnEnum.content);
      presentation.isDirty();
      resolve();
    });
  };

  return (
    <RoSPageContext.Provider
      value={{
        selectedTheme,
        selectedThemeId,
        isLoadingTemplates,
        showGenerateWithAIModal,
        setShowGenerateWithAIModal,
        saveState,
        templateId,
        selectedPanelBtn,
        toggleSelectedPanelBtn,
        view,
        toggleView,
        slides,
        setSlides,
        selectedSlideIndex,
        updateSelectedSlideIndex,
        switchSlide,
        addNewSlide,
        deleteSlide,
        batchDeleteSlides,
        duplicateSlide,
        changeInteraction,
        selectedSlide,
        mergeOrReplaceTemplate,
        rosTitle,
        layoutMode,
        updateLayoutMode,
        updateRoSTitle,
        selectPresentation,
        unLinkPresentation,
        saveTemplate,
        duplicateTemplate,
        deleteTemplate,
        session,
        updateTheme,
        handleDnD,
        onCopyInteractionLink,
        onCopyInteraction,
        onPasteInteraction,
        disableAutoFocus,
        setDisableAutoFocus,
        selectedSlideIds,
        setSelectedSlideIds,
        undo,
        redo,
        canUndo,
        canRedo,
        isAutoSaveEnabled,
        setIsAutoSaveEnabled,
        isOffline,
        retryCount,
        lastLocalSave,
        deletePendingSlides,
        handleMultiDnD,
        presentation,
        applySelectedTransitionToAllSlides,
        addSlides,
      }}
    >
      {children}
    </RoSPageContext.Provider>
  );
}

export const useRoSContext = () => {
  const context = React.useContext(RoSPageContext);
  if (!context) {
    throw new Error(
      'useRoSContext can only be used within an RoSContextProvider'
    );
  }
  return context;
};

export default RoSContext;
